import type { ComponentType } from "react";
import { cn } from "@/lib/utils";

/**
 * ONE DOMAIN SWITCHER, TWO INSTANCES — P080 D-125 (PO 2026-09-21).
 *
 * *"I want to have a button where the user can switch between WhatsApp, LinkedIn, but I wanna have
 * these all visible with the icon properly … make sure that the default is Email. Make it as
 * generic as possible."*
 *
 * WHY IT EXISTS. A surface is no longer named after ONE of its channels: the Messages surface will
 * hold Email, WhatsApp and LinkedIn, and the capital surface will hold Equity Raise, Procurement
 * and Recruitment. This is the ONE control that says which domain you are looking at — so the same
 * shell can carry domains that do not exist yet without inventing a new page for each of them, and
 * without a second renderer of the same idea (the rule D-133-023 already enforces for the queue).
 *
 * WHY THE OPTIONS ARE NOT ALL BUTTONS. An option that is not built yet is rendered as a labelled
 * STATEMENT, not a control: visible, with its own icon (the PO's point — the shape of what is
 * coming must be legible), saying "coming soon" in the words the product already uses
 * (`UploadAnythingPage.tsx`), and impossible to click into a state that does not exist. A live
 * option is a real button. That is the difference between announcing a roadmap and shipping a dead
 * control — which this product refuses to do.
 *
 * DEFAULT. `firstLiveKey()` DERIVES the default from the option list instead of hardcoding it, so
 * making a new domain live can never leave the default pointing at the wrong one.
 */

export type DomainOption = {
  key: string;
  label: string;
  /**
   * ANY icon component taking `className` — deliberately not a `LucideIcon`: a BRAND mark is not a
   * line icon, and the channels this switcher names (WhatsApp, LinkedIn) have official marks that
   * lucide does not carry.
   */
  icon: ComponentType<{ className?: string }>;
  /**
   * An optional colour class applied to the icon — how a brand keeps its own colour (`text-[#25D366]`
   * for WhatsApp, `text-[#0A66C2]` for LinkedIn) without this component knowing any brand. The
   * colour belongs beside the label it names, at the call site, not buried in the switcher.
   */
  tone?: string;
  /** `false` = visible, named, honestly labelled "coming soon" — never a silent no-op. */
  live: boolean;
};

export const firstLiveKey = (options: DomainOption[]): string =>
  options.find((option) => option.live)?.key ?? options[0]?.key ?? "";

export const DomainSwitcher = ({
  ariaLabel,
  options,
  value,
  onChange,
}: {
  ariaLabel: string;
  options: DomainOption[];
  value: string;
  /**
   * Optional on purpose: a surface where only ONE domain is live yet (the capital surface today —
   * Equity Raise live, Procurement and Recruitment announced) has nothing to switch TO, so the
   * callback is never passed and can never fire. Every non-live option renders as a statement, not
   * a button, so no click reaches this.
   */
  onChange?: (key: string) => void;
}) => (
  <div
    role="group"
    aria-label={ariaLabel}
    className="flex flex-wrap items-center gap-2"
  >
    {options.map((option) => {
      const Icon = option.icon;
      if (!option.live) {
        return (
          <div
            key={option.key}
            className="flex items-center gap-2 rounded-lg border border-dashed bg-card px-3 py-2 text-muted-foreground"
            title={`${option.label} — coming soon`}
          >
            <Icon className={cn("h-4 w-4", option.tone)} aria-hidden />
            <span className="text-sm font-medium">{option.label}</span>
            <span className="text-[0.65rem] font-medium uppercase tracking-wide">
              coming soon
            </span>
          </div>
        );
      }
      const selected = option.key === value;
      return (
        <button
          key={option.key}
          type="button"
          onClick={() => onChange?.(option.key)}
          aria-pressed={selected}
          className={cn(
            "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
            selected
              ? "border-primary bg-primary/10 text-foreground"
              : "border-border bg-card text-muted-foreground hover:text-foreground",
          )}
        >
          <Icon className={cn("h-4 w-4", option.tone)} aria-hidden />
          {option.label}
        </button>
      );
    })}
  </div>
);
